/** Stationery invitation types: builder sections (generalInfo, frontDesign, backDesign), paper size, card type. */

// ============================================================================
// STATUS
// ============================================================================

export type StationeryInvitationStatus =
  | 'draft'
  | 'ready'
  | 'ordered'
  | 'cancelled'

// ============================================================================
// PAPER SIZE & CARD TYPE
// ============================================================================

export const PAPER_SIZES = ['5x7', '4x6'] as const
export type PaperSize = (typeof PAPER_SIZES)[number]

/** Card type: Flat or Folded (5×7). */
export const CARD_TYPES = ['flat', 'folded'] as const
export type CardType = (typeof CARD_TYPES)[number]

/** Paper size → print dimensions (px at 300 DPI). */
export const PAPER_SIZE_INFO: Record<
  PaperSize,
  { label: string; description: string; widthPx: number; heightPx: number }
> = {
  '5x7': {
    label: '5" × 7"',
    description: 'Standard invitation',
    widthPx: 1500,
    heightPx: 2100,
  },
  '4x6': {
    label: '4" × 6"',
    description: 'Smaller / Save the date',
    widthPx: 1200,
    heightPx: 1800,
  },
}

/** 5×7 product SKUs (API). */
export const STATIONERY_5X7_FLAT_SKU = 'STATIONERY-5X7'
export const STATIONERY_5X7_FOLDED_SKU = 'STATIONERY-5X7-FOLDED'

export const BUYABLE_PAPER_SIZES: PaperSize[] = ['5x7']

export const STATIONERY_5X7_SKUS = [
  STATIONERY_5X7_FLAT_SKU,
  STATIONERY_5X7_FOLDED_SKU,
] as const

/** @deprecated Use CARD_TYPE_TO_PRODUCT_SKU for 5×7 flat/folded. */
export const PAPER_SIZE_TO_PRODUCT_SKU: Record<PaperSize, string> = {
  '5x7': STATIONERY_5X7_FLAT_SKU,
  '4x6': 'STATIONERY-4X6',
}

/** Card type → product SKU (Add button routing). */
export const CARD_TYPE_TO_PRODUCT_SKU: Record<CardType, string> = {
  flat: STATIONERY_5X7_FLAT_SKU,
  folded: STATIONERY_5X7_FOLDED_SKU,
}

export const PRODUCT_SKU_TO_PAPER_SIZE: Record<string, PaperSize> = {
  [STATIONERY_5X7_FLAT_SKU]: '5x7',
  [STATIONERY_5X7_FOLDED_SKU]: '5x7',
  'STATIONERY-4X6': '4x6',
}

export const PRODUCT_SKU_TO_CARD_TYPE: Record<string, CardType> = {
  [STATIONERY_5X7_FLAT_SKU]: 'flat',
  [STATIONERY_5X7_FOLDED_SKU]: 'folded',
}

export function normalizePaperSize(value: unknown): PaperSize {
  const s = typeof value === 'string' ? value.toLowerCase() : ''
  if (s === '5x7') return '5x7'
  if (s === '4x6') return '4x6'
  return '5x7'
}

// ============================================================================
// BUILDER SECTIONS (from @kiltechco/dday-templates/stationery getBuilderSections)
// ============================================================================

/** Config entry for a single builder control (select, toggle, text, etc.) */
export interface BuilderSectionConfig {
  id: string
  type?: string
  label?: string
  options?: Array<{ value: string; label: string; [key: string]: unknown }>
  defaultValue?: unknown
  [key: string]: unknown
}

/** One section in the stationery builder (General Info, Front Design, Back Design, Print Settings) */
export interface BuilderSection {
  id: string
  label: string
  icon?: string
  /** Optional long-form title (e.g. "Additional Options after design built") */
  tipTitle?: string
  /** Optional body copy shown under the section title */
  tipText?: string
  description?: string
  configs: Record<string, BuilderSectionConfig>
}

// ============================================================================
// GENERAL INFO - HOST INFO
// ============================================================================

export interface HostField {
  label?: string
  name: string
}

export interface HostInfoData {
  fields: HostField[]
}

// ============================================================================
// GENERAL INFO - DATE TIME
// ============================================================================

export interface DateTimeData {
  /** ISO datetime string e.g. "2026-06-15T16:00:00" */
  eventDateTime: string
  /** IANA timezone e.g. "America/New_York" */
  timezone?: string
}

// ============================================================================
// GENERAL INFO - LOCATION
// ============================================================================

export interface LocationData {
  venueName?: string
  /** Full address (can include newlines) */
  address: string
}

// ============================================================================
// GENERAL INFO - COMBINED SECTION
// ============================================================================

export interface GeneralInfoData {
  hostInfo: HostInfoData
  dateTime: DateTimeData
  location: LocationData
}

// ============================================================================
// FRONT DESIGN - IMAGE (matches digital hero.images)
// ============================================================================

/**
 * Image item structure - matches digital invitation's hero.images
 */
export interface FrontDesignImage {
  /** CDN URL of uploaded image */
  imageUrl?: string
  /** Alt text for accessibility */
  alt?: string
  /** Optional caption */
  caption?: string
}

// ============================================================================
// FRONT DESIGN - COMBINED SECTION (matches digital hero)
// ============================================================================

/**
 * Front design data - structure matches digital invitation's hero section
 * for easy data transfer between digital and stationery invitations
 */
export interface FrontDesignData {
  /** Color theme preset ID e.g. "royal-black", "elegant-white" */
  colorTheme?: string
  /** Selected design ID (same as hero.selectedDesign) */
  selectedDesign: string
  /** Images array (same as hero.images) */
  images?: FrontDesignImage[] | null
  /** Text layout position for designs that support it */
  layout?: 'top' | 'center' | 'bottom' | 'left' | 'right'
  /** Show design title on front page (default: true) */
  showDesignTitle?: boolean
  /** Enable date display (same as hero.enableDate, default: true) */
  enableDate?: boolean
  /** Image extends to edge with no border (default: true) */
  trimToEdge?: boolean
}

// ============================================================================
// BACK DESIGN - GREETINGS
// ============================================================================

export interface GreetingsData {
  /** Opening line e.g. "Together with their families" (max 100 chars) */
  title?: string
  /** Array of paragraphs - each string is a separate paragraph with spacing between */
  message?: string[]
}

// ============================================================================
// BACK DESIGN - QR CODE
// ============================================================================

export type QRCodeSize = 'small' | 'medium' | 'large'

export const QR_CODE_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
] as const

export interface QrCodeData {
  enabled?: boolean
  /** Backend-provided QR code image URL */
  qrImageUrl?: string
  /** QR code size (default: 'medium') */
  size?: QRCodeSize
}

// ============================================================================
// BACK DESIGN - SIZE TYPES
// ============================================================================

export type HostNameSize = 'small' | 'medium' | 'large'
export type GreetingTitleSize = 'small' | 'medium' | 'large'
export type GreetingMessageSize = 'small' | 'medium' | 'large'
export type DateTimeSize = 'small' | 'medium' | 'large'
export type LocationSize = 'small' | 'medium' | 'large'

export const HOST_NAME_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
] as const

export const GREETING_TITLE_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
] as const

export const GREETING_MESSAGE_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
] as const

export const DATE_TIME_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
] as const

export const LOCATION_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
] as const

// Character limits
export const GREETING_TITLE_MAX_LENGTH = 100
export const GREETING_MESSAGE_MAX_LENGTH = 500

// ============================================================================
// BACK DESIGN - COMBINED SECTION
// ============================================================================

export interface BackDesignData {
  /** Greetings/invitation message */
  greetings?: GreetingsData
  /** Greeting title text size (default: 'medium') */
  greetingTitleSize?: GreetingTitleSize
  /** Greeting message text size (default: 'medium') */
  greetingMessageSize?: GreetingMessageSize
  /** Show date and time on back (default: true) */
  showDateTime?: boolean
  /** Date/time text size (default: 'medium') */
  dateTimeSize?: DateTimeSize
  /** Show location on back (default: true) */
  showLocation?: boolean
  /** Location text size (default: 'medium') */
  locationSize?: LocationSize
  /** Show "from" host names on back (default: true) */
  showFrom?: boolean
  /** Show host labels (e.g., "Bride", "Groom") next to names (default: true) */
  showHostLabels?: boolean
  /** Host name font size (default: 'medium') */
  hostNameSize?: HostNameSize
  /** QR code settings */
  qrCode?: QrCodeData
  /** Font family for user content (greeting title, message, host names) */
  contentFontFamily?: string
  /** Font family for system content (labels, date/time, location, QR text) */
  systemFontFamily?: string
}

// ============================================================================
// COMPLETE TEMPLATE DATA (Sent to template package)
// ============================================================================

/**
 * Complete template data structure for stationery builder
 * This is passed to StationeryPreview and exportForPrint from the template package
 */
export interface StationeryTemplateData {
  /** Paper size – builder only supports 5x7. Drives preview aspect ratio and print dimensions. */
  paperSize?: PaperSize
  /** Card type: flat or folded (5×7 only). */
  cardType?: CardType
  /** Section 1: General Information */
  generalInfo: GeneralInfoData
  /** Section 2: Front Design (includes colorTheme) */
  frontDesign: FrontDesignData
  /** Section 3: Back Design */
  backDesign?: BackDesignData
  /** Slug for back-of-card Or visit {baseUrl}/{slug} (e.g. abc123). */
  invitationSlug?: string
}

// ============================================================================
// PRINT FILES (Generated by exportForPrint)
// ============================================================================

/**
 * Individual print file metadata
 */
export interface PrintFileData {
  /** Public CDN URL */
  url: string
  /** R2 storage path */
  path: string
  /** File size in bytes */
  size: number
  /** Pixel width */
  width: number
  /** Pixel height */
  height: number
  /** SHA-256 hash for integrity verification */
  hash: string
}

/**
 * Print files generated by exportForPrint()
 * Stored in stationery_invitations.print_files JSONB
 */
export interface StationeryPrintFiles {
  front: PrintFileData
  back: PrintFileData
  /** DPI (always 300) */
  dpi: number
  /** File format */
  format: 'png' | 'jpeg'
  /** Version number (increments on re-upload) */
  version: number
  /** ISO timestamp when generated */
  generatedAt: string
}

// ============================================================================
// STATIONERY INVITATION (Database Record)
// ============================================================================

/**
 * Stationery Invitation database record
 */
export interface StationeryInvitation {
  id: string
  userId: string
  /** Optional link to digital invitation */
  invitationId: string | null
  /** Link to order (set after checkout) */
  orderId: string | null
  /** Paper size - selected at init, determines print dimensions */
  paperSize: PaperSize
  /** Template data for preview/editing (passed to template package) */
  templateData: StationeryTemplateData
  /** Generated print-ready files */
  printFiles: StationeryPrintFiles | null
  /** Thumbnail image URL with cache-busting timestamp */
  thumbnailUrl?: string | null
  /** Title for user reference */
  title: string
  status: StationeryInvitationStatus
  createdAt: string
  updatedAt: string
  /** When status changed to 'ready' */
  readyAt: string | null
  /** When status changed to 'ordered' */
  orderedAt: string | null
  /** When status changed to 'cancelled' (soft delete) */
  cancelledAt: string | null
}

/**
 * Lightweight version for list/grid views
 */
export interface StationeryInvitationListItem {
  id: string
  title: string
  /** Paper size for display */
  paperSize: PaperSize
  /** Card type: flat or folded (from backend, defaults to 'flat') */
  cardType: CardType
  /** Design ID for thumbnail */
  designId: string
  /** Thumbnail image URL with cache-busting timestamp */
  thumbnailUrl?: string | null
  /** Event date/time (for display, extracted from templateData) */
  eventDateTime?: string | null
  invitationId: string | null
  /** Whether this stationery can be added to cart (ready with print files) */
  canAddToCart: boolean
  /** Whether this stationery has been purchased */
  isOrdered: boolean
  createdAt: string
  updatedAt: string
}

// ============================================================================
// API REQUESTS
// ============================================================================

/**
 * Initialize new stationery request
 */
export interface InitStationeryRequest {
  /** Optional client-generated UUID */
  id?: string
  /** Optional link to digital invitation */
  invitationId?: string
  /** Paper size (required - determines dimensions) */
  paperSize: PaperSize
  /** Initial design selection */
  designId?: string
  /** Initial template data */
  templateData?: Partial<StationeryTemplateData>
  /** Title for reference */
  title?: string
}

/**
 * Update stationery request
 */
export interface UpdateStationeryRequest {
  templateData?: Partial<StationeryTemplateData>
  title?: string
}

// ============================================================================
// API RESPONSES
// ============================================================================

export interface StationeryListResponse {
  success: boolean
  data: StationeryInvitationListItem[]
  total: number
}

export interface StationeryDetailResponse {
  success: boolean
  data: StationeryInvitation
}

export interface StationeryInitResponse {
  success: boolean
  message: string
  data: StationeryInvitation
}

// ============================================================================
// BUILDER STATE TYPES
// ============================================================================

export type StationeryBuilderTab = 'front' | 'back' | 'style' | 'print'
export type StationeryActivePage = 'front' | 'back'

export interface CanvasState {
  activePage: StationeryActivePage
  zoom: number
  showBleedLines: boolean
  showSafeZone: boolean
}

/**
 * Props for StationeryPreview component from template package
 */
export interface StationeryPreviewComponentProps {
  templateData: StationeryTemplateData
  activePage?: StationeryActivePage
  mode?: 'preview' | 'edit'
  showBleedLines?: boolean
  showSafeZone?: boolean
  showPageToggle?: boolean
  zoom?: number
  /** Max width in px – package scales the preview to fit */
  maxWidth?: number
  /** Max height in px – package scales the preview to fit */
  maxHeight?: number
  onPageChange?: (page: StationeryActivePage) => void
  className?: string
}

// ============================================================================
// CONSTANTS - UI Options
// ============================================================================

// No additional UI option constants needed - HOST_NAME_SIZES defined inline above

// ============================================================================
// DEFAULT DATA
// ============================================================================

/** Default paper size for new stationery (builder only supports 5×7). */
export const DEFAULT_PAPER_SIZE: PaperSize = '5x7'

/** Default card type for new stationery. */
export const DEFAULT_CARD_TYPE: CardType = 'flat'

/**
 * Default template data. Only keys the template reads: generalInfo, frontDesign, backDesign.
 * paperSize and cardType live in root store and are merged when passing to template/API.
 */
export const DEFAULT_TEMPLATE_DATA: StationeryTemplateData = {
  // Section 1: General Information
  generalInfo: {
    hostInfo: {
      fields: [],
    },
    dateTime: {
      eventDateTime: '',
      timezone: '',
    },
    location: {
      venueName: '',
      address: '',
    },
  },
  // Section 2: Front Design (matches digital hero structure)
  frontDesign: {
    colorTheme: 'royal-black',
    selectedDesign: 'tying-the-knot',
    images: [],
    showDesignTitle: true,
    enableDate: true,
    trimToEdge: true,
  },
  // Section 3: Back Design
  backDesign: {
    greetings: {
      title: '',
      message: [''],
    },
    greetingTitleSize: 'medium',
    greetingMessageSize: 'medium',
    showDateTime: true,
    dateTimeSize: 'medium',
    showLocation: true,
    locationSize: 'medium',
    showFrom: true,
    showHostLabels: true,
    hostNameSize: 'medium',
    qrCode: {
      enabled: true,
      qrImageUrl: '',
      size: 'medium',
    },
    contentFontFamily: 'Cormorant Garamond, serif',
    systemFontFamily: 'Inter, sans-serif',
  },
}
